// src/pages/laboratory/TransactionsPage.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { 
  CreditCard,
  Search,
  Filter,
  SortAsc,
  SortDesc,
  Eye,
  Calendar,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Users,
  Package,
  MapPin,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  BarChart3,
  PieChart,
  Download,
  RefreshCw,
  FileText,
  Building,
  User,
  Tag,
  QrCode,
  Activity,
  Percent,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react';
import { format, startOfMonth, endOfMonth, subMonths, isWithinInterval } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'react-toastify';

// Import your existing components
import ModalComponent from '../../components/common/ModalComponent';

// Import API functions
import { GET_LABORATORY_BY_USER } from '../../api/laboratory-api';
import { authApi } from '../../api/auth-api';

import { gql } from '@apollo/client';

const GET_LAB_TRANSACTIONS = gql`
  query GetLabTransactions($laboratoryId: ID!) {
    transactionsByLaboratory(laboratoryId: $laboratoryId) {
      id
      date
      gain
      companyCommission
      status
      type
      avantGain
      apresGain
      createdAt
      updatedAt
      user {
        id
        email
        firstName
        lastName
        userType
        tel
      }
      product {
        id
        name
        price
        image
        laboratoryId
      }
      pharmacy {
        id
        namePharmacy
        telPharmacy
        region { id nomRegion }
        ville { id nomVilleVillage }
      }
      label {
        id
        code
        date
        status
        promotion { id name }
      }
    }
  }
`;

const TransactionsPage = () => {
  // State management
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [regionFilter, setRegionFilter] = useState('all');
  const [villeFilter, setVilleFilter] = useState('all');
  const [productFilter, setProductFilter] = useState('all');
  const [promotionFilter, setPromotionFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedDateRange, setSelectedDateRange] = useState({
    start: startOfMonth(new Date()),
    end: endOfMonth(new Date())
  });

  // Get current user data
  const userData = authApi.getUserData();
  const userId = userData?.id;

  // Fetch laboratory data for current user
  const { 
    data: laboratoryData, 
    loading: laboratoryLoading,
    error: laboratoryError 
  } = useQuery(GET_LABORATORY_BY_USER, {
    variables: { userId },
    skip: !userId,
    fetchPolicy: 'cache-and-network'
  });

  const laboratory = laboratoryData?.laboratoryByUserId;

  // Fetch this lab's transactions — scoped by laboratoryId, no polling
  const {
    data: transactionsData,
    loading: transactionsLoading,
    error: transactionsError,
    refetch: refetchTransactions
  } = useQuery(GET_LAB_TRANSACTIONS, {
    variables: { laboratoryId: laboratory?.id || '' },
    skip: !laboratory?.id,
    fetchPolicy: 'cache-and-network',
  });

  const labTransactions = transactionsData?.transactionsByLaboratory || [];

  // Derive filter options from transactions
  const filterOptions = useMemo(() => {
    const regions = new Map();
    const villes = new Map();
    const products = new Map();
    const promotions = new Map();

    labTransactions.forEach(t => {
      if (t.pharmacy?.region?.nomRegion) regions.set(t.pharmacy.region.nomRegion, t.pharmacy.region.nomRegion);
      if (t.pharmacy?.ville?.nomVilleVillage) villes.set(t.pharmacy.ville.nomVilleVillage, { label: t.pharmacy.ville.nomVilleVillage, region: t.pharmacy.region?.nomRegion });
      if (t.product?.id) products.set(t.product.id, t.product.name);
      if (t.label?.promotion?.id) promotions.set(t.label.promotion.id, { name: t.label.promotion.name, productId: t.product?.id });
    });

    // Filter villes by selected region
    const filteredVilles = regionFilter === 'all'
      ? Array.from(villes.entries())
      : Array.from(villes.entries()).filter(([, v]) => v.region === regionFilter);

    // Filter promotions by selected product
    const filteredPromotions = productFilter === 'all'
      ? Array.from(promotions.entries())
      : Array.from(promotions.entries()).filter(([, p]) => p.productId === productFilter);

    return {
      regions: Array.from(regions.keys()).sort(),
      villes: filteredVilles.map(([k, v]) => ({ key: k, label: v.label })).sort((a, b) => a.label.localeCompare(b.label)),
      products: Array.from(products.entries()).map(([k, v]) => ({ id: k, name: v })).sort((a, b) => a.name.localeCompare(b.name)),
      promotions: filteredPromotions.map(([k, v]) => ({ id: k, name: v.name })).sort((a, b) => a.name.localeCompare(b.name)),
    };
  }, [labTransactions, regionFilter, productFilter]);

  // Apply filters and sorting
  const filteredTransactions = useMemo(() => {
    let filtered = [...labTransactions];

    // Text search
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(transaction =>
        transaction.product?.name.toLowerCase().includes(term) ||
        transaction.user?.firstName?.toLowerCase().includes(term) ||
        transaction.user?.lastName?.toLowerCase().includes(term) ||
        transaction.user?.email?.toLowerCase().includes(term) ||
        transaction.pharmacy?.namePharmacy?.toLowerCase().includes(term) ||
        transaction.label?.code?.toLowerCase().includes(term)
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(transaction => transaction.status === statusFilter);
    }

    // Type filter
    if (typeFilter !== 'all') {
      filtered = filtered.filter(transaction => transaction.type === typeFilter);
    }

    // Region filter
    if (regionFilter !== 'all') {
      filtered = filtered.filter(t => t.pharmacy?.region?.nomRegion === regionFilter);
    }

    // Ville filter
    if (villeFilter !== 'all') {
      filtered = filtered.filter(t => t.pharmacy?.ville?.nomVilleVillage === villeFilter);
    }

    // Product filter
    if (productFilter !== 'all') {
      filtered = filtered.filter(t => t.product?.id === productFilter);
    }

    // Promotion filter
    if (promotionFilter !== 'all') {
      filtered = filtered.filter(t => t.label?.promotion?.id === promotionFilter);
    }

    // Date filter
    if (dateFilter !== 'all') {
      const now = new Date();
      let dateRange;
      
      switch (dateFilter) {
        case 'today':
          dateRange = {
            start: new Date(now.getFullYear(), now.getMonth(), now.getDate()),
            end: new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1)
          };
          break;
        case 'week':
          dateRange = {
            start: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000),
            end: now
          };
          break;
        case 'month':
          dateRange = selectedDateRange;
          break;
        case 'quarter':
          dateRange = {
            start: subMonths(now, 3),
            end: now
          };
          break;
        default:
          dateRange = null;
      }

      if (dateRange) {
        filtered = filtered.filter(transaction => 
          isWithinInterval(new Date(transaction.date), dateRange)
        );
      }
    }

    // Sort
    filtered.sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];
      
      if (sortBy === 'date' || sortBy === 'createdAt') {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      } else if (sortBy === 'gain') {
        aVal = parseFloat(aVal);
        bVal = parseFloat(bVal);
      } else if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal?.toLowerCase() || '';
      }
      
      if (sortOrder === 'desc') {
        return bVal > aVal ? 1 : -1;
      } else {
        return aVal > bVal ? 1 : -1;
      }
    });

    return filtered;
  }, [labTransactions, searchTerm, statusFilter, typeFilter, dateFilter, regionFilter, villeFilter, productFilter, promotionFilter, sortBy, sortOrder, selectedDateRange]);

  // Reset to page 1 when filters change
  const prevFilters = React.useRef({ searchTerm, statusFilter, typeFilter, dateFilter, regionFilter, villeFilter, productFilter, promotionFilter });
  useEffect(() => {
    const prev = prevFilters.current;
    if (prev.searchTerm !== searchTerm || prev.statusFilter !== statusFilter ||
        prev.typeFilter !== typeFilter || prev.dateFilter !== dateFilter ||
        prev.regionFilter !== regionFilter || prev.villeFilter !== villeFilter ||
        prev.productFilter !== productFilter || prev.promotionFilter !== promotionFilter) {
      setCurrentPage(1);
      prevFilters.current = { searchTerm, statusFilter, typeFilter, dateFilter, regionFilter, villeFilter, productFilter, promotionFilter };
    }
  }, [searchTerm, statusFilter, typeFilter, dateFilter, regionFilter, villeFilter, productFilter, promotionFilter]);

  // Reset ville when region changes
  useEffect(() => { setVilleFilter('all'); }, [regionFilter]);
  // Reset promotion when product changes
  useEffect(() => { setPromotionFilter('all'); }, [productFilter]);

  // Calculate statistics
  const stats = useMemo(() => {
    const totalTransactions = filteredTransactions.length;
    const totalRevenue = filteredTransactions.reduce((sum, t) => sum + t.gain, 0);
    const totalCommission = filteredTransactions.reduce((sum, t) => sum + (t.gain * t.companyCommission / 100), 0);
    const avgTransactionValue = totalTransactions > 0 ? totalRevenue / totalTransactions : 0;
    const totalScans = filteredTransactions.filter(t => t.type === 'SCAN').length;
    const totalProductValue = filteredTransactions
      .filter(t => t.type === 'SCAN')
      .reduce((sum, t) => sum + (t.product?.price || 0), 0);
    const vendorCommission = totalRevenue; // gain = what the vendor (pharmacy) earns
    
    const statusCounts = filteredTransactions.reduce((acc, t) => {
      acc[t.status] = (acc[t.status] || 0) + 1;
      return acc;
    }, {});

    const topPharmacies = Object.values(
      filteredTransactions.reduce((acc, t) => {
        if (t.pharmacy) {
          const key = t.pharmacy.id;
          if (!acc[key]) {
            acc[key] = {
              pharmacy: t.pharmacy,
              count: 0,
              revenue: 0
            };
          }
          acc[key].count++;
          acc[key].revenue += t.gain;
        }
        return acc;
      }, {})
    ).sort((a, b) => b.revenue - a.revenue).slice(0, 5);

    const topProducts = Object.values(
      filteredTransactions.reduce((acc, t) => {
        if (t.product) {
          const key = t.product.id;
          if (!acc[key]) {
            acc[key] = {
              product: t.product,
              count: 0,
              revenue: 0
            };
          }
          acc[key].count++;
          acc[key].revenue += t.gain;
        }
        return acc;
      }, {})
    ).sort((a, b) => b.count - a.count).slice(0, 5);

    return {
      totalTransactions,
      totalRevenue,
      totalCommission,
      avgTransactionValue,
      totalScans,
      totalProductValue,
      vendorCommission,
      statusCounts,
      topPharmacies,
      topProducts
    };
  }, [filteredTransactions]);

  // Helper functions
  const getStatusDisplay = (status) => {
    switch(status) {
      case 'IN_PROGRESS':
        return { text: 'En cours', className: 'bg-blue-100 text-blue-800', icon: Clock };
      case 'VALIDATED':
        return { text: 'Validée', className: 'bg-green-100 text-green-800', icon: CheckCircle };
      case 'PENDING':
        return { text: 'En attente', className: 'bg-yellow-100 text-yellow-800', icon: AlertCircle };
      case 'CANCELLED':
        return { text: 'Annulée', className: 'bg-red-100 text-red-800', icon: XCircle };
      default:
        return { text: status, className: 'bg-gray-100 text-gray-800', icon: AlertCircle };
    }
  };

  const getTypeDisplay = (type) => {
    switch(type) {
      case 'SCAN':
        return { text: 'Scan QR', className: 'bg-purple-100 text-purple-800', icon: QrCode };
      case 'WITHDRAWAL':
        return { text: 'Retrait', className: 'bg-orange-100 text-orange-800', icon: CreditCard };
      default:
        return { text: type, className: 'bg-gray-100 text-gray-800', icon: Activity };
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-MA', {
      style: 'currency',
      currency: 'MAD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const exportTransactions = () => {
    // Create CSV content
    const headers = ['Date', 'Type', 'Produit', 'Utilisateur', 'Pharmacie', 'Montant', 'Commission', 'Statut'];
    const csvContent = [
      headers.join(','),
      ...filteredTransactions.map(t => [
        format(new Date(t.date), 'dd/MM/yyyy HH:mm'),
        getTypeDisplay(t.type).text,
        t.product?.name || '-',
        `${t.user?.firstName || ''} ${t.user?.lastName || ''}`.trim(),
        t.pharmacy?.namePharmacy || '-',
        t.gain,
        t.companyCommission,
        getStatusDisplay(t.status).text
      ].join(','))
    ].join('\n');

    // Download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `transactions_${laboratory?.nom || 'laboratoire'}_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    link.click();
    
    toast.success('Export des transactions téléchargé');
  };

  // Event handlers
  const handleViewTransaction = (transaction) => {
    setSelectedTransaction(transaction);
    setIsViewModalOpen(true);
  };

  const handleRefresh = () => {
    refetchTransactions();
    toast.success('Transactions actualisées');
  };

  // Pagination
  const totalPages = Math.ceil(filteredTransactions.length / pageSize);
  const paginatedTransactions = filteredTransactions.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  if (laboratoryLoading && !laboratory) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-green-700 border-r-transparent"></div>
        <p className="ml-3 text-gray-600">Chargement de votre laboratoire...</p>
      </div>
    );
  }

  if (laboratoryError || !laboratory) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-center">
          <AlertCircle className="h-6 w-6 text-red-600 mr-3" />
          <div>
            <h3 className="text-lg font-medium text-red-800">Erreur</h3>
            <p className="text-red-700">
              {laboratoryError ? laboratoryError.message : 'Laboratoire non trouvé. Contactez l\'administrateur.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-gradient-to-r from-purple-700 to-indigo-600 text-white p-4 sm:p-6 rounded-lg shadow-lg">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center min-w-0">
            <CreditCard className="w-6 h-6 sm:w-8 sm:h-8 mr-2 sm:mr-3 flex-shrink-0" />
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold">Transactions</h1>
              <p className="text-purple-100 text-sm hidden sm:block">
                Suivi des transactions de vos produits - {laboratory.nom}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-xs text-purple-100">Total</p>
              <p className="text-xl font-bold">{stats.totalTransactions}</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleRefresh}
                className="flex items-center px-3 py-2 bg-white bg-opacity-20 text-white rounded-md hover:bg-opacity-30 transition"
                title="Actualiser"
              >
                <RefreshCw size={15} />
              </button>
              <button
                onClick={exportTransactions}
                className="flex items-center px-3 py-2 text-sm bg-white text-purple-700 rounded-md hover:bg-purple-50 transition shadow-sm"
              >
                <Download size={15} className="mr-1.5" />
                <span className="hidden sm:inline">Exporter</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 sm:gap-4">
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-green-500">
          <div className="flex items-center">
            <QrCode className="h-8 w-8 text-green-600 mr-3" />
            <div>
              <p className="text-sm font-medium text-gray-600">Total Scans</p>
              <p className="text-2xl font-bold text-green-700">
                {stats.totalScans.toLocaleString()}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                Valeur: {formatCurrency(stats.totalProductValue)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-blue-500">
          <div className="flex items-center">
            <Percent className="h-8 w-8 text-blue-600 mr-3" />
            <div>
              <p className="text-sm font-medium text-gray-600">Commission vendeur</p>
              <p className="text-2xl font-bold text-blue-700">
                {formatCurrency(stats.vendorCommission)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-orange-500">
          <div className="flex items-center">
            <DollarSign className="h-8 w-8 text-orange-600 mr-3" />
            <div>
              <p className="text-sm font-medium text-gray-600">Commission Mazid</p>
              <p className="text-2xl font-bold text-orange-700">
                {formatCurrency(stats.totalCommission)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-yellow-500">
          <div className="flex items-center">
            <BarChart3 className="h-8 w-8 text-yellow-600 mr-3" />
            <div>
              <p className="text-sm font-medium text-gray-600">Valeur Moyenne</p>
              <p className="text-2xl font-bold text-yellow-700">
                {formatCurrency(stats.avgTransactionValue)}
              </p>
            </div>
          </div>
        </div>

        {(() => {
          const fund = laboratory?.commissionFund ?? 0;
          const remaining = laboratory?.soldeRestant ?? (fund - (laboratory?.commissionUsed ?? 0));
          const used = fund - remaining;
          const pct = fund > 0 ? Math.min(100, (used / fund) * 100) : 0;
          const color = fund === 0 ? 'gray' : remaining < 0 ? 'red' : remaining < fund * 0.2 ? 'yellow' : 'green';
          const borderColor = { gray: 'border-gray-400', red: 'border-red-500', yellow: 'border-yellow-500', green: 'border-green-500' }[color];
          const textColor = { gray: 'text-gray-500', red: 'text-red-700', yellow: 'text-yellow-700', green: 'text-green-700' }[color];
          const iconColor = { gray: 'text-gray-400', red: 'text-red-500', yellow: 'text-yellow-500', green: 'text-green-600' }[color];
          return (
            <div className={`bg-white p-4 rounded-lg shadow border-l-4 ${borderColor}`}>
              <div className="flex items-center mb-2">
                <DollarSign className={`h-8 w-8 mr-3 ${iconColor}`} />
                <div>
                  <p className="text-sm font-medium text-gray-600">Solde dotation</p>
                  <p className={`text-2xl font-bold ${textColor}`}>
                    {formatCurrency(remaining)}
                  </p>
                  {fund > 0 && (
                    <p className="text-xs text-gray-400">sur {formatCurrency(fund)}</p>
                  )}
                </div>
              </div>
              {fund > 0 && (
                <div className="w-full bg-gray-100 rounded-full h-1.5 mt-1">
                  <div
                    className="h-1.5 rounded-full"
                    style={{ width: `${pct}%`, backgroundColor: pct >= 90 ? '#ef4444' : pct >= 70 ? '#f59e0b' : '#16a34a' }}
                  />
                </div>
              )}
            </div>
          );
        })()}
      </div>

      {/* Filters and Search */}
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="space-y-3">
        <div className="grid grid-cols-1 lg:grid-cols-6 gap-4">
          {/* Search */}
          <div className="lg:col-span-2">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {/* Status Filter */}
          <div>
            <select
              className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 rounded-md focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">Tous les statuts</option>
              <option value="IN_PROGRESS">En cours</option>
              <option value="VALIDATED">Validée</option>
              <option value="PENDING">En attente</option>
              <option value="CANCELLED">Annulée</option>
            </select>
          </div>

          {/* Type Filter */}
          <div>
            <select
              className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 rounded-md focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <option value="all">Tous les types</option>
              <option value="SCAN">Scan QR</option>
              <option value="WITHDRAWAL">Retrait</option>
            </select>
          </div>

          {/* Date Filter */}
          <div>
            <select
              className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 rounded-md focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
            >
              <option value="all">Toutes les dates</option>
              <option value="today">Aujourd'hui</option>
              <option value="week">7 derniers jours</option>
              <option value="month">Ce mois</option>
              <option value="quarter">3 derniers mois</option>
            </select>
          </div>

          {/* Sort */}
          <div className="flex space-x-2">
            <select
              className="flex-1 pl-3 pr-10 py-2 text-base border border-gray-300 rounded-md focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="date">Date</option>
              <option value="gain">Montant</option>
              <option value="status">Statut</option>
            </select>

            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
              title={`Trier par ordre ${sortOrder === 'asc' ? 'décroissant' : 'croissant'}`}
            >
              {sortOrder === 'asc' ? <SortAsc size={16} /> : <SortDesc size={16} />}
            </button>
          </div>

          {/* Per page */}
          <div>
            <select
              className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 rounded-md focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
              value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
            >
              {[10, 25, 50, 100].map(size => (
                <option key={size} value={size}>{size} par page</option>
              ))}
            </select>
          </div>
        </div>

        {/* Second filter row: Région, Ville, Produit, Promotion */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Région */}
          <div>
            <select
              className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 rounded-md focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
              value={regionFilter}
              onChange={(e) => setRegionFilter(e.target.value)}
            >
              <option value="all">Toutes les régions</option>
              {filterOptions.regions.map(r => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>

          {/* Ville */}
          <div>
            <select
              className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 rounded-md focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
              value={villeFilter}
              onChange={(e) => setVilleFilter(e.target.value)}
            >
              <option value="all">Toutes les villes</option>
              {filterOptions.villes.map(v => (
                <option key={v.key} value={v.key}>{v.label}</option>
              ))}
            </select>
          </div>

          {/* Produit */}
          <div>
            <select
              className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 rounded-md focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
              value={productFilter}
              onChange={(e) => setProductFilter(e.target.value)}
            >
              <option value="all">Tous les produits</option>
              {filterOptions.products.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>

          {/* Promotion (filtered by selected product) */}
          <div>
            <select
              className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 rounded-md focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
              value={promotionFilter}
              onChange={(e) => setPromotionFilter(e.target.value)}
              disabled={filterOptions.promotions.length === 0 && productFilter === 'all'}
            >
              <option value="all">
                {productFilter !== 'all' ? 'Toutes les promotions du produit' : 'Toutes les promotions'}
              </option>
              {filterOptions.promotions.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
        </div>
        </div>
      </div>

      {/* Top Performers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Pharmacies */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <Building className="h-5 w-5 mr-2 text-blue-600" />
            Top Pharmacies
          </h3>
          {stats.topPharmacies.length > 0 ? (
            <ul className="space-y-3">
              {stats.topPharmacies.map((item, index) => (
                <li key={item.pharmacy.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                  <div className="flex items-center">
                    <span className="flex items-center justify-center w-6 h-6 bg-blue-100 text-blue-800 rounded-full text-sm font-medium mr-3">
                      {index + 1}
                    </span>
                    <div>
                      <p className="font-medium text-gray-900">{item.pharmacy.namePharmacy}</p>
                      <p className="text-sm text-gray-500">
                        {item.pharmacy.region?.nomRegion} - {item.pharmacy.ville?.nomVilleVillage}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-green-600">{formatCurrency(item.revenue)}</p>
                    <p className="text-sm text-gray-500">{item.count} transactions</p>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-500 text-center py-4">Aucune donnée disponible</p>
          )}
        </div>

        {/* Top Products */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <Package className="h-5 w-5 mr-2 text-green-600" />
            Produits Populaires
          </h3>
          {stats.topProducts.length > 0 ? (
            <ul className="space-y-3">
              {stats.topProducts.map((item, index) => (
                <li key={item.product.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                  <div className="flex items-center">
                    <span className="flex items-center justify-center w-6 h-6 bg-green-100 text-green-800 rounded-full text-sm font-medium mr-3">
                      {index + 1}
                    </span>
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 bg-gray-200 rounded-md overflow-hidden mr-3">
                        {item.product.image ? (
                          <img
                            src={item.product.image}
                            alt={item.product.name}
                            className="h-10 w-10 object-cover"
                          />
                        ) : (
                          <div className="h-10 w-10 flex items-center justify-center">
                            <Package className="h-5 w-5 text-gray-400" />
                          </div>
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{item.product.name}</p>
                        <p className="text-sm text-gray-500">{formatCurrency(item.product.price)}</p>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-green-600">{formatCurrency(item.revenue)}</p>
                    <p className="text-sm text-gray-500">{item.count} scans</p>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-500 text-center py-4">Aucune donnée disponible</p>
          )}
        </div>
      </div>

      {/* Transactions Table */}
      {transactionsLoading ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-purple-700 border-r-transparent"></div>
          <p className="mt-2 text-gray-600">Chargement des transactions...</p>
        </div>
      ) : filteredTransactions.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <CreditCard size={48} className="mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {labTransactions.length === 0 ? 'Aucune transaction' : 'Aucune transaction trouvée'}
          </h3>
          <p className="text-gray-500">
            {labTransactions.length === 0 
              ? "Les transactions apparaîtront ici quand vos produits seront scannés."
              : "Modifiez vos critères de recherche pour voir plus de résultats."}
          </p>
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date & Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Produit
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Utilisateur
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Pharmacie
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Montant
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedTransactions.map((transaction) => {
                  const typeDisplay = getTypeDisplay(transaction.type);
                  const TypeIcon = typeDisplay.icon;
                  
                  return (
                    <tr 
                      key={transaction.id} 
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => handleViewTransaction(transaction)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm text-gray-900">
                            {format(new Date(transaction.date), 'dd/MM/yyyy', { locale: fr })}
                          </div>
                          <div className="text-xs text-gray-500">
                            {format(new Date(transaction.date), 'HH:mm', { locale: fr })}
                          </div>
                          <div className="mt-1">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${typeDisplay.className}`}>
                              <TypeIcon className="h-3 w-3 mr-1" />
                              {typeDisplay.text}
                            </span>
                          </div>
                        </div>
                      </td>
                      
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 bg-gray-100 rounded-md overflow-hidden">
                            {transaction.product?.image ? (
                              <img
                                src={transaction.product.image}
                                alt={transaction.product.name}
                                className="h-10 w-10 object-cover"
                              />
                            ) : (
                              <div className="h-10 w-10 flex items-center justify-center">
                                <Package className="h-5 w-5 text-gray-400" />
                              </div>
                            )}
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-gray-900">
                              {transaction.product?.name || 'Produit supprimé'}
                            </div>
                            <div className="text-sm text-gray-500">
                              Prix: {formatCurrency(transaction.product?.price || 0)}
                            </div>
                          </div>
                        </div>
                      </td>
                      
                      <td className="px-6 py-4 whitespace-nowrap">
                        {transaction.user ? (
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {transaction.user.firstName} {transaction.user.lastName}
                            </div>
                            <div className="text-sm text-gray-500">
                              {transaction.user.email}
                            </div>
                            <div className="text-xs text-gray-400">
                              {transaction.user.tel}
                            </div>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-500">Utilisateur non trouvé</span>
                        )}
                      </td>
                      
                      <td className="px-6 py-4 whitespace-nowrap">
                        {transaction.pharmacy ? (
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {transaction.pharmacy.namePharmacy}
                            </div>
                            <div className="text-sm text-gray-500 flex items-center">
                              <MapPin className="h-3 w-3 mr-1" />
                              {transaction.pharmacy.region?.nomRegion}
                            </div>
                            <div className="text-xs text-gray-400">
                              {transaction.pharmacy.ville?.nomVilleVillage}
                            </div>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-500">Non spécifiée</span>
                        )}
                      </td>
                      
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-bold text-green-600">
                            {formatCurrency(transaction.gain)}
                          </div>
                          <div className="text-xs text-gray-500">
                            Commission: {transaction.companyCommission}%
                          </div>
                          <div className="text-xs text-blue-600">
                            Mazid: {formatCurrency(transaction.gain * transaction.companyCommission / 100)}
                          </div>
                        </div>
                      </td>
                      
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewTransaction(transaction);
                          }}
                          className="text-purple-600 hover:text-purple-900"
                          title="Voir les détails"
                        >
                          <Eye size={16} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-3 flex items-center justify-between border-t border-gray-200 bg-white">
              <p className="text-sm text-gray-700">
                Affichage de{' '}
                <span className="font-medium">{filteredTransactions.length === 0 ? 0 : (currentPage - 1) * pageSize + 1}</span>
                {' '}à{' '}
                <span className="font-medium">{Math.min(currentPage * pageSize, filteredTransactions.length)}</span>
                {' '}sur{' '}
                <span className="font-medium">{filteredTransactions.length}</span> résultats
              </p>
              <nav className="inline-flex rounded-md shadow-sm -space-x-px">
                <button
                  onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
                  disabled={currentPage === 1}
                  className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'}`}
                >
                  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                </button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) pageNum = i + 1;
                  else if (currentPage <= 3) pageNum = i + 1;
                  else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                  else pageNum = currentPage - 2 + i;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${currentPage === pageNum ? 'z-10 bg-purple-50 border-purple-500 text-purple-600' : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'}`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                <button
                  onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'}`}
                >
                  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" /></svg>
                </button>
              </nav>
            </div>
          )}
        </div>
      )}

      {/* Transaction Detail Modal */}
      {isViewModalOpen && selectedTransaction && (
        <ModalComponent
          isOpen={isViewModalOpen}
          onClose={() => setIsViewModalOpen(false)}
          title={`Transaction #${selectedTransaction.id.substring(0, 8)}...`}
          showFooter={false}
          size="lg"
        >
          <div className="space-y-6">
            {/* Transaction Overview */}
            <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-4 rounded-lg border border-purple-200">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-medium text-purple-900">
                  Transaction {getTypeDisplay(selectedTransaction.type).text}
                </h3>
                <span className={`px-3 py-1 inline-flex text-sm font-medium rounded-full ${
                  getStatusDisplay(selectedTransaction.status).className
                }`}>
                  {getStatusDisplay(selectedTransaction.status).text}
                </span>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-purple-700">
                    {formatCurrency(selectedTransaction.gain)}
                  </p>
                  <p className="text-sm text-purple-600">Montant total</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-blue-700">
                    {selectedTransaction.companyCommission}%
                  </p>
                  <p className="text-sm text-blue-600">Commission</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-700">
                    {formatCurrency(selectedTransaction.gain * (100 - selectedTransaction.companyCommission) / 100)}
                  </p>
                  <p className="text-sm text-green-600">Votre part</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-orange-700">
                    {formatCurrency(selectedTransaction.gain * selectedTransaction.companyCommission / 100)}
                  </p>
                  <p className="text-sm text-orange-600">Part Mazid</p>
                </div>
              </div>
            </div>

            {/* Transaction Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Date & Type Info */}
              <div className="bg-gray-50 p-4 rounded-md">
                <h4 className="text-base font-medium text-gray-900 mb-3">Informations générales</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Date:</span>
                    <span className="text-sm text-gray-900">
                      {format(new Date(selectedTransaction.date), 'dd MMMM yyyy à HH:mm', { locale: fr })}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Type:</span>
                    <span className={`px-2 py-1 inline-flex text-xs font-medium rounded-full ${
                      getTypeDisplay(selectedTransaction.type).className
                    }`}>
                      {getTypeDisplay(selectedTransaction.type).text}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">ID Transaction:</span>
                    <span className="text-sm text-gray-900 font-mono">{selectedTransaction.id.substring(0, 12)}...</span>
                  </div>
                </div>
              </div>

              {/* Financial Details */}
              <div className="bg-gray-50 p-4 rounded-md">
                <h4 className="text-base font-medium text-gray-900 mb-3">Détails financiers</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Solde avant:</span>
                    <span className="text-sm text-gray-900">{formatCurrency(selectedTransaction.avantGain)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Gain:</span>
                    <span className="text-sm font-bold text-green-600">
                      +{formatCurrency(selectedTransaction.gain)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Solde après:</span>
                    <span className="text-sm text-gray-900">{formatCurrency(selectedTransaction.apresGain)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Product Information */}
            {selectedTransaction.product && (
              <div className="bg-gray-50 p-4 rounded-md">
                <h4 className="text-base font-medium text-gray-900 mb-3">Produit concerné</h4>
                <div className="flex items-center">
                  <div className="flex-shrink-0 h-16 w-16 bg-gray-200 rounded-lg overflow-hidden">
                    {selectedTransaction.product.image ? (
                      <img
                        src={selectedTransaction.product.image}
                        alt={selectedTransaction.product.name}
                        className="h-16 w-16 object-cover"
                      />
                    ) : (
                      <div className="h-16 w-16 flex items-center justify-center">
                        <Package className="h-8 w-8 text-gray-400" />
                      </div>
                    )}
                  </div>
                  <div className="ml-4 flex-1">
                    <h5 className="text-lg font-medium text-gray-900">{selectedTransaction.product.name}</h5>
                    <p className="text-sm text-gray-600">{selectedTransaction.product.description}</p>
                    <p className="text-sm text-green-600 font-medium">
                      Prix: {formatCurrency(selectedTransaction.product.price)}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* User & Pharmacy Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* User Info */}
              {selectedTransaction.user && (
                <div className="bg-gray-50 p-4 rounded-md">
                  <h4 className="text-base font-medium text-gray-900 mb-3 flex items-center">
                    <User className="h-4 w-4 mr-2" />
                    Utilisateur
                  </h4>
                  <div className="space-y-2">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {selectedTransaction.user.firstName} {selectedTransaction.user.lastName}
                      </p>
                      <p className="text-xs text-gray-500">{selectedTransaction.user.userType}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">{selectedTransaction.user.email}</p>
                      {selectedTransaction.user.tel && (
                        <p className="text-sm text-gray-600">{selectedTransaction.user.tel}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Pharmacy Info */}
              {selectedTransaction.pharmacy && (
                <div className="bg-gray-50 p-4 rounded-md">
                  <h4 className="text-base font-medium text-gray-900 mb-3 flex items-center">
                    <Building className="h-4 w-4 mr-2" />
                    Pharmacie
                  </h4>
                  <div className="space-y-2">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {selectedTransaction.pharmacy.namePharmacy}
                      </p>
                      {selectedTransaction.pharmacy.telPharmacy && (
                        <p className="text-xs text-gray-500">{selectedTransaction.pharmacy.telPharmacy}</p>
                      )}
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <MapPin className="h-3 w-3 mr-1" />
                      <span>
                        {selectedTransaction.pharmacy.region?.nomRegion} - {selectedTransaction.pharmacy.ville?.nomVilleVillage}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* QR Code & Promotion Info */}
            {selectedTransaction.label && (
              <div className="bg-gray-50 p-4 rounded-md">
                <h4 className="text-base font-medium text-gray-900 mb-3 flex items-center">
                  <QrCode className="h-4 w-4 mr-2" />
                  Code QR & Promotion
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Code QR:</p>
                    <p className="text-sm font-mono text-gray-900">{selectedTransaction.label.code}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Scanné le: {selectedTransaction.label.date 
                        ? format(new Date(selectedTransaction.label.date), 'dd/MM/yyyy à HH:mm', { locale: fr })
                        : 'Date non disponible'}
                    </p>
                  </div>
                  {selectedTransaction.label.promotion && (
                    <div>
                      <p className="text-sm text-gray-500">Promotion:</p>
                      <p className="text-sm font-medium text-gray-900">{selectedTransaction.label.promotion.name}</p>
                      <div className="flex items-center mt-1 space-x-2">
                        <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                          {selectedTransaction.label.promotion.commissionAmount} DH
                        </span>
                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                          {selectedTransaction.label.promotion.commissionPercentage}%
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Additional Actions */}
            <div className="border-t pt-4">
              <div className="flex justify-between items-center">
                <div className="text-sm text-gray-500">
                  Créée le: {format(new Date(selectedTransaction.createdAt), 'dd/MM/yyyy à HH:mm', { locale: fr })}
                  {selectedTransaction.updatedAt !== selectedTransaction.createdAt && (
                    <span className="block">
                      Modifiée le: {format(new Date(selectedTransaction.updatedAt), 'dd/MM/yyyy à HH:mm', { locale: fr })}
                    </span>
                  )}
                </div>
                
                <div className="flex space-x-3">
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(selectedTransaction.id);
                      toast.success('ID de transaction copié');
                    }}
                    className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Copier ID
                  </button>
                </div>
              </div>
            </div>
          </div>
        </ModalComponent>
      )}
    </div>
  );
};

export default TransactionsPage;